import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    AuctionCatalogsService,
    GetAdminPaymentsByInvoiceNumber200Response,
    InvoicePayment,
} from '@viewbid/ts-angular-client';
import { Subject, takeUntil } from 'rxjs';
import { fmtDate, VbPaymentTableRow } from '@viewbid/ui';

@Component({
    selector: 'vb-invoice-payments',
    templateUrl: './invoice-payments.component.html',
    styleUrls: ['./invoice-payments.component.scss'],
})
export class InvoicePaymentsComponent implements OnInit, OnDestroy {
    constructor(
        private auctionCatalogsService: AuctionCatalogsService,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
    ) {}
    invoicePayments: InvoicePayment[] = [];
    /** Presentation rows for <vb-payment-table>, derived from invoicePayments. */
    public rows: VbPaymentTableRow[] = [];
    public isLoading: boolean = false;
    public unableToLoad: boolean = false;
    invoiceNumber: string = '';
    private destroy$: Subject<void> = new Subject<void>();
    private readonly paymentMethods: ReadonlyArray<{
        value: string;
        label: string;
    }> = [
        { value: 'Cash', label: 'Cash' },
        { value: 'InPersonCard', label: 'Card' },
        { value: 'OnlineCard', label: 'Online Card' },
        { value: 'ETransfer', label: 'E-Transfer' },
        { value: 'Paypal', label: 'PayPal' },
    ];

    /** Breadcrumb trail — same lockup as the view-invoice page. */
    get crumbTrail(): string[] {
        return ['My invoices', 'Payments'];
    }

    ngOnInit(): void {
        this.route.queryParams
            .pipe(takeUntil(this.destroy$))
            .subscribe((params: Params) => {
                this.invoiceNumber = params['invoiceNumber'];
                this.getPayments(this.invoiceNumber);
            });
    }

    private getPayments(searchedNumber: string): void {
        this.isLoading = true;
        this.auctionCatalogsService
            .getPaymentsByInvoiceNumber(searchedNumber)
            .subscribe({
                next: (
                    response: GetAdminPaymentsByInvoiceNumber200Response,
                ) => {
                    this.isLoading = false;
                    this.invoicePayments = response.data;
                    this.buildRows();
                },
                error: () => {
                    this.isLoading = false;
                    this.unableToLoad = true;
                },
            });
    }

    /** Map the API payments onto vb-payment-table presentation rows. */
    private buildRows(): void {
        this.rows = this.invoicePayments.map(
            (payment: InvoicePayment): VbPaymentTableRow => ({
                id: payment.id,
                dateLabel: fmtDate(payment.paymentDate),
                amountPaid: payment.amountPaid ?? 0,
                methodLabel: this.getPaymentMethod(payment.paymentTypes),
                owing: payment.currentOwing ?? 0,
            }),
        );
    }

    /** Back-affordance — returns to the invoices list with its search/page
     *  state intact, same as the old "Back to search result" button. */
    goBack(): void {
        this.location.back();
    }

    onCrumbSelect(index: number): void {
        if (index === 0) {
            this.router.navigate(['profiles/my-profile/my-invoices']);
        }
    }

    private getPaymentMethod(paymentMethod: string): string {
        return (
            this.paymentMethods.find(
                (method: { value: string; label: string }) =>
                    method.value === paymentMethod,
            )?.label ?? paymentMethod
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
